// OGC-446 shared overflow menu (⋯): a trigger with aria-haspopup="menu" and a
// role="menu" popover of role="menuitem" buttons. Keyboard: ArrowDown/Enter/
// Space open and focus the first item, ArrowUp/Down/Home/End move, Escape
// closes and returns focus to the trigger, and a pointerdown outside closes.
// An item click closes the menu and focuses the trigger BEFORE calling
// onSelect, so a confirmation dialog opened by the handler captures the
// trigger (not a removed menuitem) as its return-focus target. While `busy`
// the trigger stays focusable (aria-disabled) so keyboard focus is not lost
// on the action it just triggered; openMenu refuses while busy.
// `triggerText` swaps the icon trigger for a visible text button ("Manage");
// `triggerProps` adds attributes and handlers to the trigger.
// OGC-531 (SHARED-DROPDOWN.md): the popup is portaled out of clipping
// table/card containers (into the nearest dialog when inside one), shown in
// the top layer and anchored to the trigger's right edge within the viewport,
// opening above near the bottom. Type-ahead moves between items; scrolling,
// resizing or opening another dropdown closes it.
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Beacon from './Beacon.jsx';
import {
  claimOpenDropdown,
  dropdownPortalTarget,
  focusWithoutScroll as focusElement,
  positionDropdown,
  showInTopLayer,
  subscribeDropdownDismissal,
  typeAheadMatch,
} from './dropdown-popup.js';
import { IconMore } from './icons.jsx';
import './dropdown.css';

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export default function Menu({
  label = 'More actions',
  items = [],
  disabled = false,
  busy = false,
  className = '',
  triggerClassName = '',
  icon = null,
  triggerText = '',
  triggerProps = {},
  onTriggerRef = null,
}) {
  const generatedId = useId().replaceAll(':', '');
  const menuId = `bl-menu-${generatedId}`;
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pendingFocus, setPendingFocus] = useState(null);
  const releaseRef = useRef(null);
  const typeAheadRef = useRef({ buffer: '', at: 0 });
  const visibleItems = items.filter((item) => item && item.id && item.label);
  // Extra trigger behaviour (a drag grip that is also the Move menu): its
  // onKeyDown runs first and a prevented key skips the menu's own handling.
  const { onKeyDown: triggerKeyDown, ...triggerRest } = triggerProps;

  function menuItems() {
    return [...(menuRef.current?.querySelectorAll('[role="menuitem"]') || [])]
      .filter((element) => !element.disabled);
  }

  function close({ restoreFocus = true } = {}) {
    releaseRef.current?.();
    releaseRef.current = null;
    setOpen(false);
    if (restoreFocus) focusElement(triggerRef.current);
  }
  const closeRef = useRef(close);
  closeRef.current = close;

  function openMenu(focusTarget = 'first') {
    if (disabled || busy || !visibleItems.length) return;
    setPendingFocus(focusTarget);
    if (!open) releaseRef.current = claimOpenDropdown((options) => closeRef.current(options));
    setOpen(true);
  }

  useEffect(() => () => releaseRef.current?.(), []);

  useIsomorphicLayoutEffect(() => {
    if (!open || !menuRef.current) return;
    showInTopLayer(menuRef.current);
    positionDropdown(menuRef.current, triggerRef.current, 'menu');
  }, [open, visibleItems.length]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    if (pendingFocus) {
      const controls = menuItems();
      focusElement(pendingFocus === 'last' ? controls[controls.length - 1] : controls[0]);
      setPendingFocus(null);
    }

    const unsubscribe = subscribeDropdownDismissal({
      anchor: triggerRef.current,
      isInside: (target) => Boolean(rootRef.current?.contains(target) || menuRef.current?.contains(target)),
      isInsidePanel: (target) => Boolean(menuRef.current?.contains(target)),
      close: (options) => closeRef.current(options),
    });

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      if (!menuRef.current?.contains(document.activeElement)) {
        if (event.key === 'Tab') close({ restoreFocus: false });
        return;
      }
      if (event.key === 'Tab') {
        // Restore the logical tab origin before the portaled menu unmounts,
        // then let Tab move on from the trigger.
        focusElement(triggerRef.current);
        close({ restoreFocus: false });
        return;
      }
      const controls = menuItems();
      if (!controls.length) return;
      const index = controls.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        focusElement(controls[(index + 1) % controls.length]);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        focusElement(controls[(index - 1 + controls.length) % controls.length]);
      } else if (event.key === 'Home') {
        event.preventDefault();
        focusElement(controls[0]);
      } else if (event.key === 'End') {
        event.preventDefault();
        focusElement(controls[controls.length - 1]);
      } else if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        const now = Date.now();
        const match = typeAheadMatch({
          labels: controls.map((control) => control.textContent),
          activeIndex: Math.max(0, index),
          buffer: typeAheadRef.current.buffer,
          lastTypedAt: typeAheadRef.current.at,
          character: event.key,
          now,
        });
        typeAheadRef.current = { buffer: match.buffer, at: now };
        if (match.index >= 0) focusElement(controls[match.index]);
      }
    }

    // Capture so Escape closes only the menu, not an enclosing dialog.
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      unsubscribe();
      window.removeEventListener('keydown', handleKeyDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingFocus]);

  useEffect(() => {
    if (open && (disabled || busy || !visibleItems.length)) close({ restoreFocus: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, disabled, busy, visibleItems.length]);

  function handleTriggerKeyDown(event) {
    if (event.key === 'ArrowDown' || (!open && (event.key === 'Enter' || event.key === ' '))) {
      event.preventDefault();
      openMenu('first');
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openMenu('last');
    }
  }

  function select(item) {
    close();
    item.onSelect?.();
  }

  return (
    <div ref={rootRef} className={`bl-menu-root${className ? ` ${className}` : ''}`}>
      <button
        {...triggerRest}
        ref={(element) => {
          triggerRef.current = element;
          onTriggerRef?.(element);
        }}
        type="button"
        className={`${triggerText ? 'bl-menu-text-trigger' : 'bl-icon-button'}${triggerClassName ? ` ${triggerClassName}` : ''}`}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        aria-busy={busy ? 'true' : undefined}
        aria-disabled={busy ? 'true' : undefined}
        disabled={disabled}
        onClick={() => (open ? close() : openMenu('first'))}
        onKeyDown={(event) => {
          triggerKeyDown?.(event);
          if (!event.defaultPrevented) handleTriggerKeyDown(event);
        }}
      >
        {busy
          ? <Beacon size="inline" className="is-current" delayMs={0} slowAfterMs={null} />
          : triggerText
            ? <span aria-hidden="true">{triggerText}</span>
            : <span aria-hidden="true" className="bl-icon-button-glyph">{icon || <IconMore size={18} />}</span>}
      </button>
      {open && createPortal(
        <div id={menuId} role="menu" aria-label={label} className="bl-menu" ref={menuRef} popover="manual">
          {visibleItems.map((item) => (
            <div key={item.id} className="bl-menu-group">
              {item.separatorBefore && <div aria-hidden="true" className="bl-menu-separator" />}
              <button
                type="button"
                role="menuitem"
                className="bl-menu-item"
                data-tone={item.tone || undefined}
                disabled={item.disabled === true}
                tabIndex={-1}
                onClick={() => select(item)}
              >
                {item.icon ? <span aria-hidden="true" className="bl-menu-item-glyph">{item.icon}</span> : null}
                <span>{item.label}</span>
              </button>
            </div>
          ))}
        </div>,
        dropdownPortalTarget(triggerRef.current) || rootRef.current,
      )}
    </div>
  );
}
